import React from 'react';
import { Form } from '@ant-design/compatible';
import { InputNumber, Select, Spin, Switch } from 'antd';
import { nullSafeGet, nullSafeGetWithZero } from '../../utils/DataAccessUtils';
import { getRecordsForTargetCollection } from '../../reducers/standard_reducer_utils';
import {
	formatRefrigerantTypeLabel,
	getApplianceCategoryOptions,
	REFRIGERANT_TYPE_PICKER,
} from '../../utils/RefrigerantUtils';

interface AssetRefrigerantFormItemsProps {
	form: any;
	formData: any;
	/**
	 * The asset's currently selected asset type, as held by the parent form — it changes when the
	 * user picks a different type, and it arrives asynchronously on mount. Carries the class-level
	 * refrigerant configuration (`usesRefrigerant` and the three defaults) that this block seeds the
	 * toggle and the three inputs from.
	 */
	assetType: any;
	/** The buyer's `refrigerantConfig.thresholdOverrides`, so the category labels show real thresholds. */
	thresholdOverrides: any;
	refrigerantTypes: any;
	fetchRefrigerantTypes: any;
}

/**
 * The three fields the asset TYPE also configures, as (form field, asset column, asset-type column).
 * The names differ in one place — the form's `fullChargeAmount` becomes the server's `fullChargeLb`
 * — so the mapping is spelled out rather than inferred.
 */
const INHERITED_FIELDS = [
	{
		fieldName: 'applianceCategory',
		assetKey: 'applianceCategory',
		assetTypeKey: 'applianceCategory',
	},
	{ fieldName: 'fullChargeAmount', assetKey: 'fullChargeLb', assetTypeKey: 'fullChargeLb' },
	{
		fieldName: 'refrigerantTypeCode',
		assetKey: 'refrigerantTypeCode',
		assetTypeKey: 'refrigerantTypeCode',
	},
];

const isBlank = (value) => value === undefined || value === null || value === '';

/**
 * The refrigerant block of the asset form, shared by the buyer and supplier forms so the two cannot
 * drift on a set of fields that feed a regulated calculation.
 *
 * Unconnected on purpose: each parent form wires its own role's thunk, matching how every other
 * reference list on these forms is fetched.
 *
 * Registers four fields on the parent's form — `isRefrigerantTracked`, `applianceCategory`,
 * `fullChargeAmount` and `refrigerantTypeCode`. The parent turns them into the asset payload
 * through `buildRefrigerantAssetFields`; note that `fullChargeAmount` is form-only and becomes the
 * `fullChargeLb` the server stores.
 *
 * **The asset type seeds three of the four fields.** Category, full charge and refrigerant type are
 * configured in two places — on the type, describing the equipment CLASS, and here, describing THIS
 * unit — and the server resolves unit-over-class
 * (`RefrigerantLeakRateUtil.effectiveRefrigerantFields`). The class's answer is written into the
 * inputs as a real, editable VALUE rather than shown behind them as a placeholder, so what the form
 * shows is what the unit will be saved with. The consequence is deliberate: saving an asset copies
 * the class's figures onto that unit, and a later correction to the class no longer reaches units
 * saved before it — those have their own values now and have to be corrected themselves.
 */
class AssetRefrigerantFormItems extends React.Component<AssetRefrigerantFormItemsProps, any> {
	state = {
		// Mirrored into state rather than read back off the form on every render: the conditional
		// `required` validators below have to re-evaluate when the toggle moves, and a setState is
		// what re-renders this subtree.
		isRefrigerantTracked: false,
	};

	componentDidMount() {
		const { fetchRefrigerantTypes } = this.props;

		// no_pagination: the endpoint defaults to 10 rows and there are more refrigerants than that.
		fetchRefrigerantTypes({ no_pagination: true }, REFRIGERANT_TYPE_PICKER);

		this.setState({ isRefrigerantTracked: this.getEnrolmentSeed() });
	}

	/**
	 * The asset type arrives after mount (the parent fetches it) and changes again whenever the user
	 * picks a different one, so neither the toggle nor the three inherited inputs can be seeded by
	 * `initialValue` alone — antd reads that once, before the class is known.
	 *
	 * The two arrivals are deliberately not the same, and both seeders below split on it:
	 *
	 *  - the type merely FINISHING LOADING (no id, then an id) is not a user action, so it may only
	 *    fill in what the unit has not answered for itself.
	 *  - the user actually SWAPPING the type (one id for another) re-seeds unconditionally. The unit
	 *    has moved to a different class, so it takes that class's configuration, and the fields
	 *    visibly changing is what tells the user the swap did that.
	 */
	componentDidUpdate(prevProps: AssetRefrigerantFormItemsProps) {
		const previousAssetTypeId = nullSafeGet('id', prevProps.assetType);
		const assetTypeId = nullSafeGet('id', this.props.assetType);
		if (!assetTypeId || assetTypeId === previousAssetTypeId) {
			return;
		}

		const isUserChosenAssetType = !!previousAssetTypeId;
		this.seedEnrolmentFromAssetType(isUserChosenAssetType);
		this.seedInheritedFieldsFromAssetType(isUserChosenAssetType);
	}

	/**
	 * A stored enrolment is authoritative on load — it is how one appliance opts out of a tracked
	 * class, and re-seeding it here would silently re-enrol it on every visit to the form. Only an
	 * asset with no stored value, i.e. a new one, is seeded on arrival.
	 */
	seedEnrolmentFromAssetType = (isUserChosenAssetType) => {
		if (!isUserChosenAssetType && this.getStoredEnrolment() !== undefined) {
			return;
		}

		const isRefrigerantTracked = isUserChosenAssetType
			? this.getInheritedUsesRefrigerant()
			: this.getEnrolmentSeed();
		this.props.form.setFieldsValue({ isRefrigerantTracked });
		this.setState({ isRefrigerantTracked });
	};

	/**
	 * On a type SWAP every field takes the new class's answer, including a blank one: leaving the
	 * previous class's charge sitting in the box would read as though the new class supplied it.
	 */
	seedInheritedFieldsFromAssetType = (isUserChosenAssetType) => {
		const { form, formData, assetType } = this.props;

		const nextValues = INHERITED_FIELDS.reduce((values, { fieldName, assetKey, assetTypeKey }) => {
			const inheritedValue = nullSafeGetWithZero(assetTypeKey, assetType);
			if (isUserChosenAssetType) {
				return { ...values, [fieldName]: inheritedValue };
			}
			const storedValue = nullSafeGetWithZero(assetKey, formData);
			return isBlank(storedValue) ? { ...values, [fieldName]: inheritedValue } : values;
		}, {});

		if (Object.keys(nextValues).length > 0) {
			form.setFieldsValue(nextValues);
		}
	};

	/**
	 * `nullSafeGetWithZero`, not `nullSafeGet`: the latter is falsy-based, so a unit deliberately
	 * opted out of a tracked class reads as absent and would be seeded straight back on.
	 */
	getStoredEnrolment = () => nullSafeGetWithZero('isRefrigerantTracked', this.props.formData);

	getInheritedUsesRefrigerant = () =>
		!!nullSafeGetWithZero('usesRefrigerant', this.props.assetType);

	/** The unit's own answer when it has one; otherwise its class's. */
	getEnrolmentSeed = () => {
		const storedEnrolment = this.getStoredEnrolment();
		return storedEnrolment === undefined ? this.getInheritedUsesRefrigerant() : !!storedEnrolment;
	};

	/**
	 * The same unit-then-class resolution as `seedInheritedFieldsFromAssetType`, for the one render
	 * where `initialValue` is read. It covers the case where the parent already had the asset type in
	 * hand at mount; when it did not, `componentDidUpdate` fills the fields as the type arrives.
	 */
	getInheritedFieldSeed = (assetKey, assetTypeKey) => {
		const storedValue = nullSafeGetWithZero(assetKey, this.props.formData);
		return isBlank(storedValue)
			? nullSafeGetWithZero(assetTypeKey, this.props.assetType)
			: storedValue;
	};

	handleTrackedChange = (isRefrigerantTracked) => this.setState({ isRefrigerantTracked });

	render() {
		const { form, thresholdOverrides, refrigerantTypes } = this.props;
		const { getFieldDecorator } = form;
		const { isRefrigerantTracked } = this.state;

		const refrigerantTypeRecords = getRecordsForTargetCollection(
			refrigerantTypes,
			REFRIGERANT_TYPE_PICKER
		);
		// display, not `null`: sending null for a field the server reads with optValueString is worse
		// than omitting it, and hiding the inputs with CSS keeps their registered values intact for
		// the user who toggles off and straight back on. Matches the warranty fields above.
		const conditionalDisplay = { display: isRefrigerantTracked ? 'flex' : 'none' };

		return (
			<>
				{/*
				  The field is `isRefrigerantTracked` — the per-UNIT enrolment flag, seeded from the asset
				  type's class-level `usesRefrigerant` and thereafter owned by the unit. Turning it off on
				  a unit of a tracked class keeps it off: a 12 lb mini-split uses refrigerant and can still
				  sit outside the compliance programme.
				*/}
				<Form.Item label="Uses Refrigerant">
					{getFieldDecorator('isRefrigerantTracked', {
						initialValue: this.getEnrolmentSeed(),
						valuePropName: 'checked',
					})(<Switch onChange={this.handleTrackedChange} />)}
				</Form.Item>
				<Form.Item label="EPA Appliance Category" style={conditionalDisplay}>
					{getFieldDecorator('applianceCategory', {
						initialValue: this.getInheritedFieldSeed('applianceCategory', 'applianceCategory'),
						rules: [
							{
								required: isRefrigerantTracked,
								// The category selects the statutory leak-rate threshold, so without it the
								// appliance is in the ledger but can never be banded — it would read as
								// tracked while silently producing no compliance answer at all.
								message: 'Select the appliance category — it sets the leak rate threshold.',
							},
						],
					})(
						<Select placeholder="Select appliance category" allowClear>
							{getApplianceCategoryOptions(thresholdOverrides).map((option) => (
								<Select.Option value={option.value} key={option.value}>
									{option.label}
								</Select.Option>
							))}
						</Select>
					)}
				</Form.Item>
				<Form.Item label="Full Refrigerant Charge Capacity" style={conditionalDisplay}>
					{getFieldDecorator('fullChargeAmount', {
						initialValue: this.getInheritedFieldSeed('fullChargeLb', 'fullChargeLb'),
						rules: [
							{
								required: isRefrigerantTracked,
								// This is the leak rate's denominator. Absent, there is no rate and no band.
								message: 'Enter the full charge — it is the leak rate denominator.',
							},
						],
					})(
						<InputNumber
							style={{ width: '100%' }}
							min={0}
							// 5 dp is the column's precision (DECIMAL(14,5)); truncating here would
							// silently alter a regulated number.
							precision={5}
							// Pounds is the only unit offered and the only one `full_charge_lb` stores, so
							// the unit is a fixed addon rather than a picker. It stays VISIBLE: the stored
							// value is a regulated denominator, and a bare number invites a kg entry.
							addonAfter="lb"
							placeholder="Full charge capacity"
						/>
					)}
				</Form.Item>
				<Form.Item label="Refrigerant type" style={conditionalDisplay}>
					{getFieldDecorator('refrigerantTypeCode', {
						initialValue: this.getInheritedFieldSeed('refrigerantTypeCode', 'refrigerantTypeCode'),
					})(
						<Select
							placeholder="Select refrigerant type"
							allowClear
							notFoundContent={
								refrigerantTypes.fetching ? <Spin size="small" /> : 'No refrigerant types'
							}
						>
							{refrigerantTypeRecords.map((refrigerantType) => (
								<Select.Option value={refrigerantType.code} key={refrigerantType.code}>
									{formatRefrigerantTypeLabel(refrigerantType)}
								</Select.Option>
							))}
						</Select>
					)}
				</Form.Item>
			</>
		);
	}
}

export default AssetRefrigerantFormItems;
